import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PendingThreadStatus } from "@/lib/pendingThreadStore";

type PendingThreadStatusBannerProps = {
  status: PendingThreadStatus | "idle" | "verified";
  lastError?: string | null;
  onRetry?: () => void;
  isRetrying?: boolean;
  className?: string;
};

export function PendingThreadStatusBanner({
  status,
  lastError,
  onRetry,
  isRetrying,
  className,
}: PendingThreadStatusBannerProps) {
  // DS EDIT 5aug26 — banner only when a server save actually FAILED (local_only +
  // a recorded error). Fresh drafts and the normal syncing phase stay silent; the
  // compact header badge already communicates those states.
  if (status !== "local_only" || !lastError) return null;

  const isSyncing = isRetrying === true;

  return (
    <div
      role="status"
      className={cn(
        "flex flex-wrap items-center gap-2 border-b px-3 py-2 text-sm",
        "border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100",
        className
      )}
    >
      {isSyncing ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
      ) : (
        <AlertTriangle className="h-4 w-4 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <span className="font-medium">Couldn't save to the database.</span>{" "}
        Your conversation is safe in this browser and we'll keep retrying automatically.
        {lastError ? (
          <span className="mt-0.5 block text-xs opacity-80">{lastError}</span>
        ) : null}
      </div>
      {onRetry ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 shrink-0"
          disabled={isSyncing}
          onClick={onRetry}
        >
          {isSyncing ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
          )}
          Retry save
        </Button>
      ) : null}
    </div>
  );
}

export function PendingThreadStatusBadge({
  status,
}: {
  status: PendingThreadStatus | "idle" | "verified";
}) {
  if (status === "idle" || status === "synced_partial") return null;

  const label =
    status === "syncing"
      ? "Syncing"
      : status === "verified"
        ? "Saved"
        : status === "local_only"
          ? "Local only"
          : null;
  if (!label) return null;

  const Icon =
    status === "syncing" ? Loader2 : status === "verified" ? CheckCircle2 : AlertTriangle;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        status === "local_only" && "bg-amber-500/15 text-amber-800 dark:text-amber-200",
        status === "syncing" && "bg-primary/10 text-primary",
        status === "verified" && "bg-emerald-500/15 text-emerald-800 dark:text-emerald-200"
      )}
    >
      <Icon className={cn("h-3 w-3", status === "syncing" && "animate-spin")} />
      {label}
    </span>
  );
}
